import React, { useState } from 'react';
import axios from 'axios';
import {
  ClipboardCheck, Snowflake, Package as PackageIcon, Truck,
  MapPin, ShieldCheck, Loader2,
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Six MVP checkpoint actions. Order mirrors the operational sequence:
 *   register_product → cold_storage → packaging → transport → distribution → consumer
 */
const ACTIONS = [
  { id: 'register_product',      label: 'Register Product',     icon: ClipboardCheck },
  { id: 'move_to_cold_storage',  label: 'Move To Cold Storage', icon: Snowflake },
  { id: 'package_product',       label: 'Package Product',      icon: PackageIcon },
  { id: 'start_transport',       label: 'Start Transport',      icon: Truck },
  { id: 'arrive_at_destination', label: 'Arrive At Destination',icon: MapPin },
  { id: 'customer_verification', label: 'Customer Verification',icon: ShieldCheck },
];

const SM_ALLOWED = new Set(['customer_verification']);

const ProductTraceabilityCheckpoints = ({ batchId, role, onRecorded }) => {
  const visibleActions = role === 'sustainability_manager'
    ? ACTIONS.filter((a) => SM_ALLOWED.has(a.id))
    : ACTIONS;
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(null); // action id when in-flight

  const submit = async (action) => {
    if (submitting) return;
    setSubmitting(action.id);
    try {
      const token = localStorage.getItem('mvp_token');
      await axios.post(
        `${BACKEND_URL}/api/batches/${batchId}/checkpoints`,
        {
          action: action.id,
          location: location.trim() || null,
          notes: notes.trim() || null,
        },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {},
      );
      toast.success(`${action.label} recorded`);
      setNotes('');
      if (typeof onRecorded === 'function') await onRecorded();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to record checkpoint';
      toast.error(msg);
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <section
      data-testid="product-checkpoints-section"
      className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 lg:p-8"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3 sm:mb-4">
        <div className="min-w-0">
          <h2
            className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-800"
            style={{ fontFamily: 'Manrope' }}
          >
            Product Traceability Checkpoints
          </h2>
        </div>
      </div>

      {/* Optional context fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
        <Field label="Location (optional)">
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="e.g. Oslo Test Site"
            data-testid="checkpoint-location-input"
            className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400"
          />
        </Field>
        <Field label="Notes (optional)">
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add a short note"
            data-testid="checkpoint-notes-input"
            className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400"
          />
        </Field>
      </div>

      {/* Action grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
        {visibleActions.map((a) => {
          const isBusy = submitting === a.id;
          const Icon = a.icon;
          return (
            <Button
              key={a.id}
              type="button"
              variant="outline"
              data-testid={`checkpoint-btn-${a.id}`}
              disabled={!!submitting}
              onClick={() => submit(a)}
              className="justify-start h-11 sm:h-12 text-sm sm:text-base font-medium border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800"
            >
              {isBusy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Icon className="w-4 h-4 mr-2 text-slate-600" />
              )}
              {a.label}
            </Button>
          );
        })}
      </div>

      <p className="mt-3 text-[11px] sm:text-xs text-slate-500">
        Each checkpoint is timestamped, attributed to the signed-in user, and (when available)
        attaches the latest sensor temperature reading for this batch.
      </p>
    </section>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[11px] sm:text-xs uppercase tracking-wider text-slate-500 font-semibold">
      {label}
    </span>
    <div className="mt-1">{children}</div>
  </label>
);

export default ProductTraceabilityCheckpoints;
